import { Component, ReactNode } from 'react';

import { ErrorPage } from '../../pages/Error/ErrorPage';
import { LoadingPage } from '../../pages/Loading/LoadingPage';
import config from '../../config';
import { goToUrl } from '../../utils/goToUrl';
import {
  RedirectToLoginError,
  RedirectToRootError,
  isRedirectToLoginError,
} from './errors';

interface ErrorBoundaryState {
  isRedirecting: boolean;
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  loginUrl: string;
  rootPath?: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      isRedirecting: false,
      hasError: false,
      error: undefined,
    };
  }

  static getDerivedStateFromError(error: unknown) {
    switch (true) {
      case error instanceof RedirectToLoginError:
      case error instanceof RedirectToRootError:
        return { isRedirecting: true, error };
      default:
        return { hasError: true, error };
    }
  }

  componentDidCatch(error: unknown) {
    const { loginUrl, rootPath } = this.props;

    switch (true) {
      case error instanceof RedirectToLoginError:
        goToUrl(
          isRedirectToLoginError(error) && error.loginUrl
            ? error.loginUrl
            : loginUrl
        );
        break;
      case error instanceof RedirectToRootError:
        goToUrl(
          `${config.basePath}${config.pathPrefix}/${rootPath ? rootPath : ''}`
        );
        break;
      default:
        break;
    }
  }

  render() {
    if (this.state.isRedirecting) {
      return <LoadingPage />;
    }
    if (this.state.hasError) {
      return <ErrorPage error={this.state.error} />;
    }
    return this.props.children;
  }
}
